import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Button,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormGroup,
  Checkbox,
  Switch,
  Paper,
  Slider
} from '@mui/material';
import { 
  Tune, 
  ContactEmergency, 
  Pets, 
  Home,
  Business,
  Groups,
  Phone,
  PersonPin,
  Warning,
  CheckCircle,
  Star,
  Security
} from '@mui/icons-material';

const PreferencesStep = ({ formData, updateFormData, errors }) => {
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return digits;
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    updateFormData({ [field]: value });
  };

  const handlePhoneChange = (field) => (event) => {
    const formatted = formatPhoneNumber(event.target.value);
    updateFormData({ [field]: formatted });
  };

  const handleRadioChange = (field) => (event) => {
    const value = event.target.value === 'true';
    updateFormData({ [field]: value });
  };

  const handleSwitchChange = (field) => (event) => {
    const value = event.target.checked;
    updateFormData({ [field]: value });
  };

  const handleCheckboxChange = (field) => (event) => {
    const isChecked = event.target.checked;
    const currentPreferences = formData.clientTypePreferences || [];
    
    if (isChecked) {
      updateFormData({ 
        clientTypePreferences: [...currentPreferences, field] 
      });
    } else {
      updateFormData({ 
        clientTypePreferences: currentPreferences.filter(pref => pref !== field) 
      });
    }
  };

  const handleSliderChange = (field) => (event, value) => {
    updateFormData({ [field]: value });
  };

  const clientTypes = [
    { value: 'residential', label: 'Residential Homes', icon: <Home />, description: 'Houses, apartments, condos' },
    { value: 'commercial', label: 'Commercial Spaces', icon: <Business />, description: 'Offices, retail stores, medical facilities' },
    { value: 'vacation_rental', label: 'Vacation Rentals', icon: <Star />, description: 'Airbnb, VRBO, short-term rentals' },
    { value: 'post_construction', label: 'Post-Construction', icon: <Security />, description: 'New builds, renovation cleanup' }
  ];

  const communicationMethods = [
    { value: 'text', label: 'Text Messages' },
    { value: 'phone', label: 'Phone Calls' },
    { value: 'email', label: 'Email' },
    { value: 'app', label: 'Mobile App Notifications' }
  ];

  const getHourlyRateDisplay = (value) => {
    return `$${value}/hour`;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Preferences & Emergency Contact
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Set your work preferences and provide emergency contact information for safety.
      </Typography>

      {/* Work Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tune color="primary" /> Work Preferences
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          {/* Client Type Preferences */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
            Preferred Client Types
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select the types of clients you prefer to work with. This helps us match you with suitable opportunities.
          </Typography>

          <Grid container spacing={2}>
            {clientTypes.map((type) => (
              <Grid item xs={12} sm={6} key={type.value}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    border: (formData.clientTypePreferences || []).includes(type.value) ? 2 : 1,
                    borderColor: (formData.clientTypePreferences || []).includes(type.value) ? 'primary.main' : 'grey.300'
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(formData.clientTypePreferences || []).includes(type.value)}
                          onChange={handleCheckboxChange(type.value)}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {type.label}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {type.description}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Rate Expectations */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
            Hourly Rate Expectations
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Set your preferred hourly rate. We'll help match you with clients within your range.
          </Typography>

          <Box sx={{ px: 2 }}>
            <Slider
              value={formData.preferredHourlyRate || 25}
              onChange={handleSliderChange('preferredHourlyRate')}
              min={15}
              max={35}
              step={5}
              marks={[
                { value: 15, label: '$15' },
                { value: 25, label: '$25' },
                { value: 35, label: '$35+' }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={getHourlyRateDisplay}
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
              {getHourlyRateDisplay(formData.preferredHourlyRate || 25)} - This is your preferred rate, actual rates may vary by job type
            </Typography>
          </Box>

          {/* Special Considerations */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
            Special Considerations
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Pets />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Comfortable Working with Pets
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Many clients have cats, dogs, or other pets in their homes
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={formData.comfortableWithPets || false}
                  onChange={handleSwitchChange('comfortableWithPets')}
                  color="primary"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Groups />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Willing to Work in Teams
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Some larger jobs require multiple cleaners working together
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={formData.willingToWorkInTeams || false}
                  onChange={handleSwitchChange('willingToWorkInTeams')}
                  color="primary"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Skills or Certifications"
                placeholder="e.g., Green cleaning certified, carpet cleaning experience, commercial cleaning training..."
                value={formData.specialSkills || ''}
                onChange={handleChange('specialSkills')}
                helperText="Any special training, certifications, or unique skills you bring to cleaning"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Communication Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Phone color="primary" /> Communication Preferences
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            How would you prefer to receive job notifications and communicate with clients?
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Preferred Communication Method</InputLabel>
            <Select
              value={formData.preferredCommunicationMethod || ''}
              onChange={handleChange('preferredCommunicationMethod')}
              label="Preferred Communication Method"
            >
              {communicationMethods.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Receive Job Notifications via SMS
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Get instant text notifications for new job opportunities
              </Typography>
            </Box>
            <Switch
              checked={formData.receiveSmsNotifications || false}
              onChange={handleSwitchChange('receiveSmsNotifications')}
              color="primary"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContactEmergency color="primary" /> Emergency Contact Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Required for Safety:</strong> This information is kept confidential and only used in case of emergencies 
              while you're working at client locations.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Emergency Contact Name"
                placeholder="Full name of emergency contact"
                value={formData.emergencyContactName || ''}
                onChange={handleChange('emergencyContactName')}
                error={!!errors.emergencyContactName}
                helperText={errors.emergencyContactName || 'Spouse, parent, sibling, or close friend'}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Relationship"
                placeholder="e.g., Spouse, Parent, Sibling, Friend"
                value={formData.emergencyContactRelationship || ''}
                onChange={handleChange('emergencyContactRelationship')}
                error={!!errors.emergencyContactRelationship}
                helperText={errors.emergencyContactRelationship}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Emergency Contact Phone"
                placeholder="(555) 123-4567"
                value={formData.emergencyContactPhone || ''}
                onChange={handlePhoneChange('emergencyContactPhone')}
                error={!!errors.emergencyContactPhone}
                helperText={errors.emergencyContactPhone || 'Primary phone number'}
                inputProps={{ maxLength: 14 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Alternative Phone (Optional)"
                placeholder="(555) 123-4567"
                value={formData.emergencyContactAltPhone || ''}
                onChange={handlePhoneChange('emergencyContactAltPhone')}
                helperText="Secondary contact number"
                inputProps={{ maxLength: 14 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Emergency Contact Address (Optional)"
                placeholder="123 Main Street, City, State ZIP"
                value={formData.emergencyContactAddress || ''}
                onChange={handleChange('emergencyContactAddress')}
                helperText="Emergency contact's address for reference"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Privacy Notice */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'warning.light', borderRadius: 1 }}>
        <Typography variant="body2" color="warning.dark">
          <Warning sx={{ verticalAlign: 'middle', mr: 1 }} />
          <strong>Privacy & Safety:</strong> Your emergency contact information is encrypted and stored securely. 
          It's only accessible to authorized personnel and used exclusively for safety purposes during work assignments.
        </Typography>
      </Box>
    </Box>
  );
};

export default PreferencesStep;